import React, {useEffect, useState} from 'react';
import {Button, createListCollection, HStack, Input, Portal, Select, Spinner, Textarea, VStack} from '@chakra-ui/react';
import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {DatePicker, Field, LanguageField} from "@/components";
import {useLanguage} from "@/contexts/languageContext";

export const CreateOrEditEvent = (props) => {
    const eventId = props?.id;

    const { hourFormat, t } = useLanguage();
    const [state, setState] = useState(eventId ? -1 : 1);
    const {getLangObject} = useLanguage();
    const [event, setEvent] = useState(null);
    const api = useApi();

    const eventTypeCollection = createListCollection({
        items: [
            {label: t('events.typeEvent'), value: 'EVENT'},
            {label: t('events.typeWebinar'), value: 'WEBINAR'},
        ]
    });

    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            title: getLangObject(),
            description: getLangObject(),
            startDate: null,
            endDate: null,
            type: 'EVENT',
            link: '',
        },
        {
            title: {
                custom: {
                    function: (value) => {
                        return Object.values(value).every((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
            description: {
                custom: {
                    function: (value) => {
                        const values = Object.values(value);
                        const toCheck = values[0];
                        return values.every((value) => (toCheck === '' && value === toCheck) || (toCheck.length >= 3 && value.length >= 3))
                    },
                    message: t('errors.pleaseFillOrKeepEmpty')
                },
            },
            startDate: {
                required: {
                    value: true,
                    message: t('errors.pleaseFillField')
                },
            },
            endDate: {
                required: {
                    value: true,
                    message: t('errors.pleaseFillField')
                },
            }
        }
    );

    useEffect(() => {
        if (eventId) {
            setState(() => 0);
            api.ApiService.getOne('event', eventId, {
                attributes: ['id', 'title', 'description', 'startDate', 'endDate', 'type', 'link'],
            })
                .then((res) => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        setEvent(() => res.data.value);
                    }
                })
        }
    }, []);

    useEffect(() => {
        if (event !== null) {
            handleInputChanges({
                ...event,
                startDate: new Date(event.startDate) ?? null,
                endDate: new Date(event.endDate) ?? null
            });
            setState(() => 1)
        }
    }, [event]);

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                state === 1 ?
                    <VStack overflow="auto" flex={1} gap="5" p="5" w="100%" align="center" justify="flex-start">
                        <VStack flex={1} gap="8" w="100%" h="80vh" position="relative">
                            <LanguageField
                                id="title"
                                overflow="hidden"
                                errors={formFieldErrors.title}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.title') })}
                                renderElement={(lang) => <Input
                                    value={formData.title[lang]}
                                    onChange={(e) => {
                                        handleInputChange('title', {...formData.title, [lang]: e?.target?.value ?? ''})
                                    }}
                                    placeholder={t('events.eventTitle')}
                                />}
                            />
                            <Field
                                w="unset"
                                label={t('wrappers.mandatoryLabel', { value: t('events.eventType') })}
                            >
                                <Select.Root
                                    onValueChange={(e) => handleInputChange('type', e?.value?.[0] ?? 'EVENT')}
                                    collection={eventTypeCollection}
                                    value={[formData.type]}
                                >
                                    <Select.HiddenSelect/>
                                    <Select.Control>
                                        <Select.Trigger>
                                            <Select.ValueText/>
                                        </Select.Trigger>
                                        <Select.IndicatorGroup>
                                            <Select.Indicator/>
                                        </Select.IndicatorGroup>
                                    </Select.Control>
                                    <Portal>
                                        <Select.Positioner>
                                            <Select.Content>
                                                {eventTypeCollection.items.map((item) => (
                                                    <Select.Item item={item} key={`event-type-${item.value}`}>
                                                        {item.label}
                                                        <Select.ItemIndicator/>
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Positioner>
                                    </Portal>
                                </Select.Root>
                            </Field>
                            {
                                formData.type === 'WEBINAR' &&
                                <Field
                                    w="100%"
                                    label={t('events.eventLink')}
                                    errors={formFieldErrors.link}
                                >
                                    <Input
                                        value={formData.link}
                                        onChange={(e) => handleInputChange('link', e?.target?.value ?? '')}
                                        placeholder={t('events.eventLink')}
                                    />
                                </Field>
                            }
                            <HStack w="100%">
                                <Field
                                    w="unset"
                                    label={t('wrappers.mandatoryLabel', { value: t('fields.startingDate') })}
                                    errors={formFieldErrors.startDate}
                                >
                                    <DatePicker
                                        showTimeSelect={true}
                                        date={formData.startDate}
                                        onChange={(e) => handleInputChange('startDate', e)}
                                        dateFormat={`EEE d MMM ${hourFormat}`}
                                        label={t('fields.startingDate')}
                                    />
                                </Field>
                                <Field
                                    w="unset"
                                    label={t('wrappers.mandatoryLabel', { value: t('fields.endingDate') })}
                                    errors={formFieldErrors.endDate}
                                >
                                    <DatePicker
                                        showTimeSelect={true}
                                        date={formData.endDate}
                                        onChange={(e) => handleInputChange('endDate', e)}
                                        label={t('fields.endingDate')}
                                        dateFormat={`EEE d MMM ${hourFormat}`}
                                        {
                                        ...formData.startDate
                                            ? {
                                                minDate: formData.startDate,
                                            }
                                            : {
                                                disabled: true
                                            }
                                        }
                                    />
                                </Field>
                            </HStack>
                            <LanguageField
                                id="description"
                                overflow="hidden"
                                errors={formFieldErrors.title}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.description') })}
                                renderElement={(lang) => <Textarea
                                    w="100%"
                                    resize="none"
                                    minH="8rem"
                                    maxH="8rem"
                                    overflowY="auto"
                                    overflowX="none"
                                    value={formData.description[lang]}
                                    onChange={(e) => {
                                        handleInputChange('description', {...formData.description, [lang]: e?.target?.value ?? ''})
                                    }}
                                    placeholder={t('events.eventDescription')}
                                />}
                            />
                        </VStack>
                    </VStack>
                    :
                    <VStack flex={1} w="100%" align="center" justify="flex-start">
                        <Spinner size="lg"/>
                    </VStack>
            }
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: '1rem 1.5rem'}}
                onClick={() => handleFormSubmit(async (data) => {
                    if (eventId) {
                        return api.ApiService.update('event', eventId, data);
                    }
                    return api.ApiService.create('event', data);
                }, props?.successCallback)
                }
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditEvent;
